type DateValue =
    | string
    | number
    | Date
    | { seconds: number }
    | { toDate: () => Date };

const toDate = (value: DateValue | undefined | null): Date | null => {
    if (value === undefined || value === null) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
        return (value as { toDate: () => Date }).toDate();
    }
    if (typeof (value as { seconds?: number }).seconds === 'number') {
        return new Date((value as { seconds: number }).seconds * 1000);
    }
    return null;
};

export const toDateMs = (value: DateValue | undefined | null): number | null => {
    const date = toDate(value);
    if (!date) return null;
    const time = date.getTime();
    return Number.isNaN(time) ? null : time;
};

/**
 * Formats a date value into a relative time string (e.g., "5m", "2h", "1d").
 */
export const formatRelativeTime = (value: DateValue): string => {
    const date = toDate(value);
    if (!date) return '';

    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
        return 'Just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return `${diffInMinutes}m`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return `${diffInHours}h`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
        return `${diffInDays}d`;
    }

    // Fallback to simple date for older items
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
